import React, { Fragment } from "react";
import _ from "lodash";
import { CREATION_TIME_SECTION } from "./constants";
import request from "../../infra/request";
import getSMSLogs from "./services/getSMSLogs";
import moment from "moment";
import { BOOKING_STATUS_CLASSES_MAP, GP_REPORT_STATUS_CLASSES_MAP, GP_REPORT_STATUS_MAP } from "../constant";
import { AUTO_SMS_KEY, CLIENT_APPOINTMENTS } from "../../constants";
import { getSentSMSByName } from "./services/getSentSMS";
import { getVerifiedOutboundLetters } from "../../utils/gpReport";
import { isGPReportListEnabled } from "../../infra/featureToggle";

export const generateCreationTimestamp = (ca, type = CREATION_TIME_SECTION.CREATION_TIME) => {
  const sentAutoSMS = _.get(ca, "sms.sent", null);
  const initialFirstSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.INITIAL);

  const creationTimestampMapping = {
    [CREATION_TIME_SECTION.CREATION_TIME]: ca.creationTimestamp,
    [CREATION_TIME_SECTION.FIRST_SMS_TIME]: initialFirstSms?.sentTime,
  };

  return creationTimestampMapping[type];
};

/**
 * IANA zone for a postcode from /postcode_timezone_map, or null when the
 * postcode is missing, unmapped, or the lookup fails. Callers fall back to the
 * tenant zone. (It used to return an `{ result, error }` object on a miss,
 * which the one caller then passed to moment as a zone name.)
 */
export const getTimezone = async (referrerPostcode) => {
  if (_.isEmpty(referrerPostcode)) {
    return null;
  }
  try {
    const response = await request.get(`/admin/postcode-timezones/${encodeURIComponent(referrerPostcode)}`);
    const timezone = response.data.timezone;
    return _.isString(timezone) && !_.isEmpty(timezone) ? timezone : null;
  } catch (err) {
    console.error("postcode timezone lookup failed", err);
    return null;
  }
};

const APPOINTMENT_CYCLE_TYPES = ["RESCHEDULE", "NEW"];
const CYCLE_START_TOLERANCE_SECONDS = 10 * 60;

// Only the initial chain can open a new confirmation cycle. SECOND/THIRD/FOURTH are
// reminders: they still fire after a skipInitialSMS reschedule, so counting them here
// would retroactively open a "new cycle" when the first reminder goes out and hide the
// confirmation we already had - the exact bug this gate is meant to prevent.
const CYCLE_STARTING_SMS_NAMES = [AUTO_SMS_KEY.INITIAL, AUTO_SMS_KEY.INITIAL_SECOND, AUTO_SMS_KEY.INITIAL_THIRD];

// The traffic light has one dot per bucket; every "initial*" SMS shares the first dot.
const STATUS_BUCKETS = [AUTO_SMS_KEY.INITIAL, AUTO_SMS_KEY.SECOND, AUTO_SMS_KEY.THIRD, AUTO_SMS_KEY.FOURTH];

const toStatusBucket = (smsName) => {
  if (!smsName) {
    return null;
  }
  if (_.startsWith(smsName, AUTO_SMS_KEY.INITIAL)) {
    return AUTO_SMS_KEY.INITIAL;
  }
  return _.includes(STATUS_BUCKETS, smsName) ? smsName : null;
};

const hasAutoSmsSentSince = (ca, sinceTs) => {
  const sentAutoSMS = _.get(ca, "sms.sent", null);
  if (!sentAutoSMS) {
    return false;
  }
  return _.some(CYCLE_STARTING_SMS_NAMES, (name) => {
    const sent = getSentSMSByName(sentAutoSMS, name);
    return sent && Number(sent.sentTime) >= sinceTs;
  });
};

/**
 * A reschedule only opens a new confirmation cycle when the auto SMS chain is actually
 * restarted. Staff can reschedule with skipInitialSMS and notify the patient with a manual
 * SMS instead - no auto SMS follows the change, so resetting the cycle there would hide the
 * reply we already have and leave the traffic light blank (SE-672 follow-up).
 *
 * With multiple reschedules, the latest one may skip the initial SMS while an earlier one
 * restarted the chain. Walk cycle timestamps newest → oldest and return the most recent
 * one that actually has an initial-chain auto SMS sent since it; otherwise return 0.
 */
export const getSmsCycleStartedAt = (ca) => {
  const timestamps = _.chain(_.get(ca, "appointmentChanges", []))
    .filter((change) => _.includes(APPOINTMENT_CYCLE_TYPES, _.get(change, "type", "")))
    .map((change) => Number(_.get(change, "timestamp", 0)))
    .filter((ts) => Number.isFinite(ts) && ts > 0)
    .orderBy([], ["desc"])
    .value();

  if (_.isEmpty(timestamps)) {
    return 0;
  }

  for (const ts of timestamps) {
    const cycleStartedAt = Math.max(ts - CYCLE_START_TOLERANCE_SECONDS, 0);
    if (hasAutoSmsSentSince(ca, cycleStartedAt)) {
      return cycleStartedAt;
    }
  }

  return 0;
};

/**
 * 0: not sent
 * 1: sent but no yes or no response
 * 2: sent with yes response
 * 3: sent with no response
 */
export const getReceivedYesStatus = (id, clientAppointments) => {
  const renderMark = (status, key) => {
    let className = "";
    switch (status) {
      case "":
        className = "has-text-link";
        break;
      case "yes":
        className = "has-text-success";
        break;
      case "no":
        className = "has-text-danger";
        break;
      case "no_response":
        className = "has-text-warning";
        break;
      case "noFormatResponse":
        className = "has-text-danger";
        break;
      default:
    }
    //return <span key={key} className={className}>#</span>;
    return (
      <span key={key} className={`${className} icon is-small`}>
        <i className="fas fa-circle" />
      </span>
    );
  };

  const clientAppointment = _.find(clientAppointments, (ca) => ca.id === id);
  const apt = _.get(clientAppointment, "appointment", null);

  if (!apt) {
    return null;
  }

  const logs = getSMSLogs(id, clientAppointments, "sms", getSmsCycleStartedAt(clientAppointment));
  if (_.isEmpty(logs)) return "";

  let which = "";
  // A reply can land in the cycle before any auto SMS record exists: staff answered on the
  // patient's behalf ("Acting Patient"), or the booking skipped the initial SMS chain. Without
  // a fallback the answer is written to result[""], which is never rendered, so the light stays
  // dark even though we know the patient confirmed.
  const fallbackBucket = toStatusBucket(_.get(clientAppointment, "smsState.lastAction", null)) || AUTO_SMS_KEY.INITIAL;
  let result = {};
  for (let idx = 0; idx < logs.length; idx++) {
    if (_.isNil(logs[idx].text)) {
      continue;
    }

    if (logs[idx]?.name) {
      which = toStatusBucket(logs[idx].name) || logs[idx].name;
      result[which] = "";
    }

    if (logs[idx]?.name === AUTO_SMS_KEY.INITIAL_SECOND) {
      // if there is no received yes or no at 8 hours after initial second sms, treat it as patient says no
      if (moment().diff(moment.unix(logs[idx].ts), "hours") >= 8) {
        result[which] = "no_response";
      }
    }

    if (logs[idx].patient) {
      const bucket = which || fallbackBucket;
      if (logs[idx].text.replace(/(\r\n|\n|\r)/gm, " ").search(/\byes\b|\by\b/gi) !== -1) {
        result[bucket] = "yes";
      } else {
        result[bucket] = "no";
      }
    }
  }

  return [
    renderMark(_.get(result, "initial", null), 0),
    renderMark(_.get(result, "second", null), 1),
    renderMark(_.get(result, "third", null), 2),
    renderMark(_.get(result, "fourth", null), 3),
  ];
};

export const renderIVRIcon = (id, clientAppointments) => {
  const clientAppointment = _.find(clientAppointments, (ca) => ca.id === id);
  const appointment = _.get(clientAppointment, "appointment", null);
  if (!appointment) {
    return null;
  }
  if (_.isEmpty(_.get(clientAppointment, "client.phoneNumberMobile", ""))) {
    return null;
  }

  const ivr = _.get(clientAppointment, "ivr", null);

  let className = "",
    key = 0;

  if (ivr && ivr.reply === "1") {
    className = "has-text-success";
    key = 1;
  }

  if (ivr && ivr.reply === "2") {
    className = "has-text-warning";
    key = 2;
  }

  if (ivr && !ivr.reply && ["completed", "no-answer"].includes(ivr.status)) {
    className = "has-text-danger";
    key = 3;
  }

  return (
    <Fragment>
      <span className="dash">-</span>
      <span key={key} className={`${className} icon is-small`}>
        <i className="fas fa-circle" />
      </span>
    </Fragment>
  );
};

export const getTaskStatusClasses = (status) => {
  const className = BOOKING_STATUS_CLASSES_MAP[status];
  return className ?? "has-background-white px-2";
};

export const getGpStatusClasses = (status) => {
  const className = GP_REPORT_STATUS_CLASSES_MAP[status];
  return className ?? GP_REPORT_STATUS_CLASSES_MAP["NC"];
};

export const getGpStatus = (status) => {
  const className = GP_REPORT_STATUS_MAP[status];
  return className ?? GP_REPORT_STATUS_MAP["NC"];
};

// Text shown in the workflow table GP Report column. Shared by the cell renderer
// and column filter so keyword matching lines up with what the user sees.
export const getGpReportCellDisplayText = (ca) => {
  if (_.has(ca, "gp_report")) {
    return getGpStatus(_.get(ca, "gp_report_t.status", ""));
  }

  const outboundLetterCount = getVerifiedOutboundLetters(ca).length;
  if (isGPReportListEnabled && outboundLetterCount > 0) {
    return `View Letters (${outboundLetterCount})`;
  }

  return "";
};

// GP Report uses two sort keys: non-empty cells first, then display text A–Z / Z–A.
export const gpReportSortHasValueIteratee = (ca) => (getGpReportCellDisplayText(ca) ? 0 : 1);

export const gpReportSortDisplayTextIteratee = (ca) => getGpReportCellDisplayText(ca).toLowerCase();

export const appendGpReportSortIteratees = (columnSorts) => [
  ...columnSorts,
  gpReportSortHasValueIteratee,
  gpReportSortDisplayTextIteratee,
];

export const gpReportSortOrdersForDirection = (ascending) => ["asc", ascending ? "asc" : "desc"];

export const buildWorkflowSortOrders = (sortColumns) =>
  sortColumns.flatMap(({ column, order }) => {
    if (column === "gp_report") {
      return gpReportSortOrdersForDirection(order);
    }
    return [order ? "asc" : "desc"];
  });

// Clicked column becomes the primary sort; re-click toggles asc/desc.
export const applyWorkflowSortingClick = (sortColumns, column) => {
  const index = _.findIndex(sortColumns, { column });
  let newSortColumns = [...sortColumns];

  if (index < 0) {
    return [
      {
        column,
        order: false,
        isCurrentClickedColumn: true,
      },
      ...newSortColumns.map((sortColumn) => ({ ...sortColumn, isCurrentClickedColumn: false })),
    ];
  }

  const updatedColumn = {
    column,
    order: !sortColumns[index].order,
    isCurrentClickedColumn: true,
  };

  newSortColumns.splice(index, 1);

  return [updatedColumn, ...newSortColumns.map((sortColumn) => ({ ...sortColumn, isCurrentClickedColumn: false }))];
};

export const getRemainingWeeksToAppointment = (ca) => {
  const timestamp = _.get(ca, "creationTimestamp", "");
  let weeksToAppointment = _.get(ca, "appointment_t.weeks_to_appointment", "");
  if (_.isEmpty(weeksToAppointment)) {
    return "";
  }
  weeksToAppointment = _.toNumber(weeksToAppointment);
  const currentStatus = _.get(ca, "appointment_t.status", "");
  //Some old data doesn't have statusLastUpdateAt field
  const statusLastUpdateAt = _.get(ca, "appointment_t.statusLastUpdateAt", moment().unix());
  if (!CLIENT_APPOINTMENTS.includes(currentStatus)) {
    //Return the weeksToAppointment at status last update time if is not active status
    return moment.unix(timestamp).add(weeksToAppointment, "w").diff(moment.unix(statusLastUpdateAt), "w");
  }

  return moment.unix(timestamp).add(weeksToAppointment, "w").diff(moment(), "w");
};

export const renderCreationTimeSection = (ca, timeType) => {
  const timestamp = generateCreationTimestamp(ca, timeType);

  if (!timestamp) {
    return <span className="creation-time-not-found">Not Sent</span>;
  }

  return moment.unix(timestamp).format("YYYY-MM-DD HH:mm");
};

export const renderManualSMSStatus = (ca) => {
  const manual_sms = _.get(ca, "manual_sms");

  if (!manual_sms) {
    return null;
  }

  const { received } = manual_sms;
  const className = received ? "has-text-primary" : "";

  return (
    <span key={ca.id} className={`${className} icon is-small`}>
      <i className="fas fa-circle" />
    </span>
  );
};
